import React from "react";
import { useForm, Controller } from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { IoIosArrowBack } from "react-icons/io";
import { DatePicker } from "@mui/x-date-pickers";
import { Autocomplete, FormControl, Select, MenuItem, TextField, Checkbox,} from "@mui/material";
import dayjs from "dayjs";

export const MilestoneLogsFilterComponent = ({
  milestoneName,
  projectId,
  onApply,
  onClear,
  onCancel,
  sourceData, // The raw source data from TimesheetTable
  fromDate,
  toDate,
  prefillData,
}) => {
  // Extract unique users from sourceData
  const milestoneUsers = React.useMemo(() => {
    if (!sourceData || !Array.isArray(sourceData)) return [];
    const usersMap = new Map();
    sourceData.forEach((group) => {
      if (Array.isArray(group.subtasks)) {
        group.subtasks.forEach((log) => {
          if (log.owner_id) {
            usersMap.set(log.owner_id, log.ownerName || group.userName);
          }
        });
      }
    });
    return Array.from(usersMap.entries()).map(([id, name]) => ({
      id: Number(id),
      title: name,
    }));
  }, [sourceData]);

  // Extract unique tasks from sourceData
  const milestoneTasks = React.useMemo(() => {
    if (!sourceData || !Array.isArray(sourceData)) return [];
    const tasksMap = new Map();
    sourceData.forEach((group) => {
      if (Array.isArray(group.subtasks)) {
        group.subtasks.forEach((log) => {
          if (log.task_id || log.log_type === "TASK") {
            const taskName = log.task_general_issue || log.user;
            if (taskName) {
              const id = log.task_id || taskName;
              tasksMap.set(id, {
                id: id,
                title: taskName,
              });
            }
          }
        });
      }
    });
    return Array.from(tasksMap.values());
  }, [sourceData]);

  // Extract unique issues from sourceData
  const milestoneIssues = React.useMemo(() => {
    if (!sourceData || !Array.isArray(sourceData)) return [];
    const issuesMap = new Map();
    sourceData.forEach((group) => {
      if (Array.isArray(group.subtasks)) {
        group.subtasks.forEach((log) => {
          if (log.issue_id || log.log_type === "ISSUE") {
            const issueName = log.task_general_issue || log.user;
            if (issueName) {
              const id = log.issue_id || issueName;
              issuesMap.set(id, {
                id: id,
                title: issueName,
              });
            }
          }
        });
      }
    });
    return Array.from(issuesMap.values());
  }, [sourceData]);

  const {
    handleSubmit,
    control,
    reset,
  } = useForm({
    defaultValues: {
      Users: [],
      Tasks: [],
      Issues: [],
      StartDate: fromDate ? dayjs(fromDate, "YYYY-MM-DD") : null,
      EndDate: toDate ? dayjs(toDate, "YYYY-MM-DD") : null,
      billingType: "",
      createdBy: "",
      timesheet_type: "",
    },
  });

  // Re-sync values if prefillData changes
  React.useEffect(() => {
    if (prefillData) {
      reset({
        Users: prefillData.Users || [],
        Tasks: prefillData.Tasks || [],
        Issues: prefillData.Issues || [],
        StartDate: prefillData.StartDate ? dayjs(prefillData.StartDate) : null,
        EndDate: prefillData.EndDate ? dayjs(prefillData.EndDate) : null,
        billingType: prefillData.billingType || "",
        createdBy: prefillData.createdBy || "",
        timesheet_type: prefillData.timesheet_type || "",
      });
    } else {
      reset({
        Users: [],
        Tasks: [],
        Issues: [],
        StartDate: fromDate ? dayjs(fromDate, "YYYY-MM-DD") : null,
        EndDate: toDate ? dayjs(toDate, "YYYY-MM-DD") : null,
        billingType: "",
        createdBy: "",
        timesheet_type: "",
      });
    }
  }, [prefillData, reset, fromDate, toDate]);

  const onSubmit = (formData) => {
    onApply(formData);
  };

  const handleClear = () => {
    reset({
      Users: [],
      Tasks: [],
      Issues: [],
      StartDate: null,
      EndDate: null,
      billingType: "",
      createdBy: "",
      timesheet_type: "",
    });
    onClear();
  };

  return (
    <div>
      {/* Header Section */}
      <div className="d-flex align-items-center bg-white justify-content-between px-4 py-3">
        <button onClick={onCancel} className="R_btn">
          <div className="d-flex text-rgba-gray align-items-center">
            <IoIosArrowBack className="text-rgba-gray" />
            Filter
          </div>
        </button>
        <div>
          <button
            type="button"
            onClick={handleClear}
            className="R_btn tex-tgba-red clear-filter-btn"
          >
            Clear Filter
          </button>
        </div>
      </div>

      {/* Filter Form */}
      <div className="bg-white px-4 py-3">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="g-3 mb-2 row">
              {/* Users field */}
              <div className="col-md-12">
                <label htmlFor="Users" className="form-label">
                  Users
                </label>
                <Controller
                  name="Users"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const ALL_OPTION = { id: "all", title: "Select All" };
                    const allSelected =
                      Array.isArray(value) &&
                      milestoneUsers.length > 0 &&
                      value.length === milestoneUsers.length &&
                      milestoneUsers.every((user) => value.includes(user.id));

                    const optionsWithAll = [ALL_OPTION, ...milestoneUsers];
                    const selectedValues =
                      Array.isArray(value) && value.length === 0
                        ? []
                        : milestoneUsers.filter((user) => value?.includes(user.id));

                    const handleUserChange = (event, newValue) => {
                      const isAllSelected = newValue.some((val) => val.id === "all");

                      if (isAllSelected) {
                        const isCurrentlyAllSelected =
                          value.length === milestoneUsers.length &&
                          milestoneUsers.every((user) => value.includes(user.id));

                        if (isCurrentlyAllSelected) {
                          onChange([]);
                        } else {
                          onChange(milestoneUsers.map((user) => user.id));
                        }
                      } else {
                        onChange(newValue.map((val) => val.id));
                      }
                    };

                    return (
                      <Autocomplete
                        multiple
                        id="Users"
                        options={optionsWithAll}
                        disableCloseOnSelect
                        getOptionLabel={(option) =>
                          option.id === "all" ? "Select All" : option.title
                        }
                        value={selectedValues}
                        onChange={handleUserChange}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} className="d-flex align-items-center mb-2">
                            <Checkbox
                              style={{ marginRight: 8 }}
                              checked={option.id === "all" ? allSelected : selected}
                            />
                            <strong>{option.title}</strong>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Users" />
                        )}
                      />
                    );
                  }}
                />
              </div>

              {/* Tasks field */}
              <div className="col-md-12 mt-3">
                <label htmlFor="Tasks" className="form-label">
                  Tasks
                </label>
                <Controller
                  name="Tasks"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const ALL_OPTION = { id: "all", title: "Select All" };
                    const allSelected =
                      Array.isArray(value) &&
                      milestoneTasks.length > 0 &&
                      value.length === milestoneTasks.length &&
                      milestoneTasks.every((task) => value.includes(task.id));

                    const optionsWithAll = milestoneTasks.length > 0 ? [ALL_OPTION, ...milestoneTasks] : [];
                    const selectedValues =
                      Array.isArray(value) && value.length === 0
                        ? []
                        : milestoneTasks.filter((task) => value?.includes(task.id));

                    const handleTaskChange = (event, newValue) => {
                      const isAllSelected = newValue.some((val) => val.id === "all");

                      if (isAllSelected) {
                        const isCurrentlyAllSelected =
                          value.length === milestoneTasks.length &&
                          milestoneTasks.every((task) => value.includes(task.id));

                        if (isCurrentlyAllSelected) {
                          onChange([]);
                        } else {
                          onChange(milestoneTasks.map((task) => task.id));
                        }
                      } else {
                        onChange(newValue.map((val) => val.id));
                      }
                    };

                    return (
                      <Autocomplete
                        multiple
                        id="Tasks"
                        options={optionsWithAll}
                        disableCloseOnSelect
                        getOptionLabel={(option) =>
                          option.id === "all" ? "Select All" : option.title
                        }
                        value={selectedValues}
                        onChange={handleTaskChange}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} className="d-flex align-items-center mb-2">
                              <Checkbox
                              style={{ marginRight: 8 }}
                              checked={option.id === "all" ? allSelected : selected}
                              />
                              <strong>{option.title}</strong>
                            </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Tasks" />
                        )}
                      />
                    );
                  }}
                />
              </div>

              {/* Issues field */}
              <div className="col-md-12 mt-3">
                <label htmlFor="Issues" className="form-label">
                  Issues
                </label>
                <Controller
                  name="Issues"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const ALL_OPTION = { id: "all", title: "Select All" };
                    const allSelected =
                      Array.isArray(value) &&
                      milestoneIssues.length > 0 &&
                      value.length === milestoneIssues.length &&
                      milestoneIssues.every((issue) => value.includes(issue.id));

                    const optionsWithAll = milestoneIssues.length > 0 ? [ALL_OPTION, ...milestoneIssues] : [];
                    const selectedValues =
                      Array.isArray(value) && value.length === 0
                        ? []
                        : milestoneIssues.filter((issue) => value?.includes(issue.id));

                    const handleIssueChange = (event, newValue) => {
                      const isAllSelected = newValue.some((val) => val.id === "all");

                      if (isAllSelected) {
                        const isCurrentlyAllSelected =
                          value.length === milestoneIssues.length &&
                          milestoneIssues.every((issue) => value.includes(issue.id));

                        if (isCurrentlyAllSelected) {
                          onChange([]);
                        } else {
                          onChange(milestoneIssues.map((issue) => issue.id));
                        }
                      } else {
                        onChange(newValue.map((val) => val.id));
                      }
                    };

                    return (
                      <Autocomplete
                        multiple
                        id="Issues"
                        options={optionsWithAll}
                        disableCloseOnSelect
                        getOptionLabel={(option) =>
                          option.id === "all" ? "Select All" : option.title
                        }
                        value={selectedValues}
                        onChange={handleIssueChange}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} className="d-flex align-items-center mb-2">
                            <Checkbox
                              style={{ marginRight: 8 }}
                              checked={option.id === "all" ? allSelected : selected}
                            />
                            <strong>{option.title}</strong>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Issues" />
                        )}
                      />
                    );
                  }}
                />
              </div>

              {/* Start Date & End Date */}
              <div className="col-md-6 mt-3">
                <label htmlFor="StartDate">Start date</label>
                <div className="d-flex flex-column w-100">
                  <Controller
                    name="StartDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        format="DD/MM/YYYY"
                        onChange={(date) => field.onChange(date)}
                        renderInput={(params) => (
                          <TextField {...params} className="form-control" />
                        )}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="col-md-6 mt-3">
                <label htmlFor="EndDate">End date</label>
                <div className="d-flex flex-column w-100">
                  <Controller
                    control={control}
                    name="EndDate"
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        onChange={(date) => field.onChange(date)}
                        format="DD/MM/YYYY"
                        renderInput={(params) => (
                          <TextField {...params} className="form-control" />
                        )}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Billing Type */}
              <div className="col-md-6 mt-3">
                <label htmlFor="billingType" className="form-label">
                  Billing Type
                </label>
                <Controller
                  name="billingType"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        renderValue={(selected) =>
                          selected ? (
                            selected
                          ) : (
                            <span className="lightText">Type</span>
                          )
                        }
                      >
                        <MenuItem value="BILLABLE">BILLABLE</MenuItem>
                        <MenuItem value="NON-BILLABLE">NON-BILLABLE</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </div>

              {/* Created By */}
              <div className="col-md-6 mt-3">
                <label htmlFor="createdBy" className="form-label">
                  Created By
                </label>
                <Controller
                  name="createdBy"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        renderValue={(selected) => {
                          const matchedUser = milestoneUsers.find(
                            (user) => user.id === selected
                          );
                          return matchedUser ? (
                            matchedUser.title
                          ) : (
                            <span className="lightText">Select user</span>
                          );
                        }}
                      >
                        {milestoneUsers.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </div>

              {/* OT Status */}
              <div className="col-md-6 mt-3">
                <label htmlFor="timesheet_type" className="form-label">
                  OT Status
                </label>
                <Controller
                  name="timesheet_type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        renderValue={(selected) =>
                          selected ? (
                            selected === "REGULAR" ? "Regular" : "OT"
                          ) : (
                            <span className="lightText">Type</span>
                          )
                        }
                      >
                        <MenuItem value="REGULAR">REGULAR</MenuItem>
                        <MenuItem value="OT">OT</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="d-flex bg-white position-absolute w-100 bottom-0 mt-5 py-3">
              <button type="submit" className="add_btn me-3">
                Apply
              </button>
              <button type="button" className="cancel_btn" onClick={onCancel}>
                Cancel
              </button>
            </div>
          </form>
        </LocalizationProvider>
      </div>
    </div>
  );
};

export default MilestoneLogsFilterComponent;
